import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, X, AlertTriangle, Image, Edit2, ExternalLink } from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Logos = () => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [name, setName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [order, setOrder] = useState('0');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { fetchLogos(); }, []);

  const fetchLogos = async () => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const res = await fetch(`${API_URL}/api/logos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setLogos(data.logos);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditingLogo(null);
    setName(''); setWebsiteUrl(''); setOrder('0'); setFile(null); setPreview(null); setFormError('');
    setShowModal(true);
  };

  const openEdit = (logo) => {
    setEditingLogo(logo);
    setName(logo.name);
    setWebsiteUrl(logo.website_url || '');
    setOrder(String(logo.display_order));
    setFile(null);
    setPreview(`${API_URL}${logo.logo}`);
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLogo(null);
    setFile(null);
    setPreview(null);
    setWebsiteUrl('');
    setFormError('');
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (preview && !editingLogo) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setFormError('Naziv je obavezan'); return; }
    if (!editingLogo && !file) { setFormError('Logo slika je obavezna'); return; }
    setSubmitting(true);

    const fd = new FormData();
    fd.append('name', name);
    fd.append('website_url', websiteUrl);
    fd.append('display_order', order);
    if (file) fd.append('logo', file);

    try {
      const token = localStorage.getItem('mstechs_token');
      const url = editingLogo ? `${API_URL}/api/logos/${editingLogo.id}` : `${API_URL}/api/logos`;
      const method = editingLogo ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (data.success) { closeModal(); fetchLogos(); }
      else setFormError(data.message || 'Greška');
    } catch (err) {
      setFormError('Greška pri čuvanju');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const res = await fetch(`${API_URL}/api/logos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) { setDeleteConfirm(null); fetchLogos(); }
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="loading-state">
      <div className="loader"></div>
      <p>Učitavanje logoa...</p>
    </div>
  );

  return (
    <div className="clients-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Logoi klijenata</h2>
          <span className="page-subtitle">{logos.length} logoa — prikazuju se u traci na sajtu</span>
        </div>
        <button className="btn-add" onClick={openAdd}>
          <Plus size={18} /> Dodaj logo
        </button>
      </div>

      {logos.length === 0 ? (
        <div className="empty-state">
          <Image size={48} style={{ color: 'var(--admin-text-muted)', marginBottom: 16 }} />
          <p>Nema logoa. Dodajte prvi logo klijenta.</p>
        </div>
      ) : (
        <div className="logos-grid">
          {logos.map(logo => (
            <div key={logo.id} className="logo-admin-card">
              <div className="logo-admin-preview">
                <img src={`${API_URL}${logo.logo}`} alt={logo.name} />
              </div>
              <div className="logo-admin-info">
                <span className="logo-admin-name">{logo.name}</span>
                <span className="logo-admin-order">Redosled: {logo.display_order}</span>
                {logo.website_url && (
                  <a href={logo.website_url} target="_blank" rel="noopener noreferrer" className="project-admin-link" style={{ fontSize: 11 }}>
                    <ExternalLink size={11} /> {logo.website_url}
                  </a>
                )}
              </div>
              <div className="logo-admin-actions">
                <button className="btn-icon btn-edit" onClick={() => openEdit(logo)} title="Izmeni">
                  <Edit2 size={15} />
                </button>
                <button className="btn-icon btn-danger" onClick={() => setDeleteConfirm(logo)} title="Obriši">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal za dodavanje/izmenu */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingLogo ? 'Izmeni logo' : 'Dodaj logo klijenta'}</h3>
              <button className="modal-close" onClick={closeModal}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label>Naziv klijenta *</label>
                <input value={name} onChange={e => { setName(e.target.value); setFormError(''); }} placeholder="npr. Dudi Studio" />
              </div>
              <div className="form-group">
                <label>Link sajta (opciono)</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={e => { setWebsiteUrl(e.target.value); setFormError(''); }}
                  placeholder="https://..."
                />
              </div>
              <div className="form-group">
                <label>Redosled</label>
                <input type="number" value={order} onChange={e => setOrder(e.target.value)} min="0" />
              </div>
              <div className="form-group">
                <label>Logo slika {editingLogo ? '(ostavi prazno da zadržiš stari)' : '*'}</label>
                <div
                  className="logo-upload-zone"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } }}
                >
                  {preview ? (
                    <img src={preview} alt="Preview" className="logo-upload-preview" />
                  ) : (
                    <>
                      <Image size={32} style={{ color: 'var(--admin-text-muted)' }} />
                      <span>Klikni ili prevuci sliku ovde</span>
                      <span style={{ fontSize: 11, color: 'var(--admin-text-dark)' }}>PNG, SVG, JPG — max 5MB</span>
                    </>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
              </div>

              {formError && (
                <div className="form-error">
                  <AlertTriangle size={16} /> {formError}
                </div>
              )}

              <div className="modal-footer" style={{ padding: '0', borderTop: 'none', marginTop: 8 }}>
                <button type="button" className="btn-cancel" onClick={closeModal}>Otkaži</button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Čuvanje...' : (editingLogo ? 'Sačuvaj' : 'Dodaj')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Obriši logo</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <AlertTriangle size={24} />
                <p>Da li ste sigurni da želite da obrišete logo <strong>{deleteConfirm.name}</strong>?</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Otkaži</button>
              <button className="btn-delete" onClick={() => handleDelete(deleteConfirm.id)}>Obriši</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logos;
